import React, { createContext, useContext, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  setApplicationNotificationsBridgeContext,
  getApplicationNotificationsBridgeContext,
} from './bridge'
import { DebuggingNotifier } from './debuggingNotifier'
import { PageBannerNotifier } from './pageBannerNotifier'
import { OverlayBannerNotifier } from './overlayBannerNotifier'
import { ToastNotifier, ToastOptions } from './toastNotifier'
import { SnackbarNotifier, SnackbarOptions } from './snackbarNotifier'
import { getNullApplicationNotifications } from './getNullApplicationNotifications'
import { NOTIFICATION_ROLES, DEFAULT_NOTIFICATION_OPTIONS } from './constants'
import { NamedNotifier } from './namedNotifier'
import { useMountEffect } from '../../hooks'

export type Notifications = {
  debugger: DebuggingNotifier
  toast: ToastNotifier
  overlayBanner: OverlayBannerNotifier
  pageBanner: PageBannerNotifier
  snackbar: SnackbarNotifier
  notify: NamedNotifier
  roles: typeof NOTIFICATION_ROLES
}

export type { ToastOptions, SnackbarOptions }

const defaultOverlayBanner = new OverlayBannerNotifier()
const defaultToast = new ToastNotifier()
const defaultSnackbar = new SnackbarNotifier()
const defaultPageBanner = new PageBannerNotifier()

const ApplicationNotificationsContext = createContext<Notifications>({
  debugger: new DebuggingNotifier(),
  toast: defaultToast,
  overlayBanner: defaultOverlayBanner,
  pageBanner: defaultPageBanner,
  snackbar: defaultSnackbar,
  notify: new NamedNotifier({
    overlayBanner: defaultOverlayBanner,
    toast: defaultToast,
    snackbar: defaultSnackbar,
    pageBanner: defaultPageBanner,
  }),
  roles: NOTIFICATION_ROLES,
})

const ApplicationNotificationsConsumer =
  ApplicationNotificationsContext.Consumer

interface ApplicationNotificationsProviderProps {
  children: React.ReactNode
  pageBannerContainerRef?: React.RefObject<HTMLElement>
}

function ApplicationNotificationsProvider({
  children,
  pageBannerContainerRef,
}: ApplicationNotificationsProviderProps) {
  const notifierRef = useRef<{
    debuggerNotifier: DebuggingNotifier
    toastNotifier: ToastNotifier
    overlayBannerNotifier: OverlayBannerNotifier
    pageBannerNotifier: PageBannerNotifier
    snackbarNotifier: SnackbarNotifier
  } | null>(null)
  const [debuggerNotifier, setDebuggerNotifier] = useState(
    new DebuggingNotifier()
  )
  const [toastNotifier, setToastNotifier] = useState(new ToastNotifier())
  const [overlayBannerNotifier, setOverlayBannerNotifier] = useState(
    new OverlayBannerNotifier()
  )
  const [pageBannerNotifier, setPageBannerNotifier] = useState(
    new PageBannerNotifier()
  )
  const [snackbarNotifier, setSnackbarNotifier] = useState(
    new SnackbarNotifier()
  )

  // hold a reference to the notifiers for use in cleanup
  notifierRef.current = {
    debuggerNotifier,
    toastNotifier,
    overlayBannerNotifier,
    pageBannerNotifier,
    snackbarNotifier,
  }

  useMountEffect(() => {
    debuggerNotifier.initialize()
    toastNotifier.initialize()
    overlayBannerNotifier.initialize()
    pageBannerNotifier.initialize(pageBannerContainerRef)
    snackbarNotifier.initialize()

    // update state so context is aware of initialization
    setDebuggerNotifier(debuggerNotifier)
    setToastNotifier(toastNotifier)
    setOverlayBannerNotifier(overlayBannerNotifier)
    setPageBannerNotifier(pageBannerNotifier)
    setSnackbarNotifier(snackbarNotifier)

    return () => {
      if (notifierRef.current) {
        const {
          debuggerNotifier,
          toastNotifier,
          overlayBannerNotifier,
          pageBannerNotifier,
          snackbarNotifier,
        } = notifierRef.current

        debuggerNotifier.destroy?.()
        toastNotifier.destroy?.()
        overlayBannerNotifier.destroy?.()
        pageBannerNotifier.destroy?.()
        snackbarNotifier.destroy?.()
      }
    }
  })

  const context = {
    debugger: debuggerNotifier,
    toast: toastNotifier,
    overlayBanner: overlayBannerNotifier,
    pageBanner: pageBannerNotifier,
    snackbar: snackbarNotifier,
    notify: new NamedNotifier({
      overlayBanner: overlayBannerNotifier,
      toast: toastNotifier,
      snackbar: snackbarNotifier,
      pageBanner: pageBannerNotifier,
    }),
    roles: NOTIFICATION_ROLES,
  }

  // set the bridge context for use in redux actions (non-component access)
  setApplicationNotificationsBridgeContext(context)

  return (
    <ApplicationNotificationsContext.Provider value={context}>
      {children}
    </ApplicationNotificationsContext.Provider>
  )
}

ApplicationNotificationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  pageBannerContainerRef: PropTypes.object,
}

/**
 * For use in functional components
 */
const useApplicationNotifications = () => {
  const context = useContext(ApplicationNotificationsContext)
  return context
}

/**
 * For use with class components
 */
interface WithApplicationNotificationsContextProps {
  notifications: Notifications
}

const withApplicationNotificationsContext =
  <P extends object>(
    Component: React.ComponentType<P & WithApplicationNotificationsContextProps>
  ) =>
  (props: P) =>
    (
      <ApplicationNotificationsContext.Consumer>
        {context => <Component {...props} notifications={context} />}
      </ApplicationNotificationsContext.Consumer>
    )

/**
 * For testing purposes
 * */
interface MockApplicationNotificationsProviderProps {
  children: React.ReactNode
  context: Notifications
}
const MockApplicationNotificationsProvider = ({
  children,
  context,
}: MockApplicationNotificationsProviderProps) => (
  <ApplicationNotificationsContext.Provider value={context}>
    {children}
  </ApplicationNotificationsContext.Provider>
)

MockApplicationNotificationsProvider.propTypes = {
  children: PropTypes.node,
  context: PropTypes.object,
}

// see also ../index.js for the export (which uses named exports, not "*")
export {
  ApplicationNotificationsProvider,
  ApplicationNotificationsConsumer,
  useApplicationNotifications, // hook for functional components
  withApplicationNotificationsContext, // HOC for class components
  getApplicationNotificationsBridgeContext, // bridge for redux actions
  NOTIFICATION_ROLES,
  DEFAULT_NOTIFICATION_OPTIONS,
  getNullApplicationNotifications,
  MockApplicationNotificationsProvider,
}
